import { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

/* ── Constants ── */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MINIMUM_DONATION = 100_000;

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(amount || 0);

/* ── Sub-components ── */
const TierSelector = ({ tiers, activeTier, onSelect }) => (
  <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
    {tiers.map((tier) => {
      const selected = activeTier?.id === tier.id;
      return (
        <button
          key={tier.id}
          type="button"
          data-testid={`donation-tier-${tier.id}`}
          onClick={() => onSelect(tier.id)}
          className={`rounded-[28px] border p-5 text-left transition duration-300 ${
            selected
              ? "border-[#C5A059] bg-[#C5A059]/10 shadow-[0_0_40px_rgba(197,160,89,0.18)]"
              : "border-white/10 bg-white/5 hover:-translate-y-1 hover:border-[#C5A059]/40"
          }`}
        >
          <p className="text-xs uppercase tracking-[0.28em] text-zinc-400">{tier.label}</p>
          <p className="mt-3 font-display text-2xl text-white" data-testid={`donation-tier-amount-${tier.id}`}>
            {formatCurrency(tier.amount)}
          </p>
          <p className="mt-1 text-sm text-zinc-300">{tier.name}</p>
          <p className="mt-4 text-sm text-zinc-400">{tier.impact}</p>
        </button>
      );
    })}
  </div>
);

const CheckoutForm = ({ activeTier, form, setForm, isLoading, onSubmit }) => (
  <motion.form
    layout
    onSubmit={onSubmit}
    className="grid gap-4 sm:grid-cols-2"
    data-testid="checkout-form"
  >
    <Input
      value={form.donor_name}
      onChange={(e) => setForm((c) => ({ ...c, donor_name: e.target.value }))}
      placeholder="Donor name"
      className="luxury-input"
      required
      data-testid="checkout-donor-name-input"
    />
    <Input
      type="email"
      value={form.donor_email}
      onChange={(e) => setForm((c) => ({ ...c, donor_email: e.target.value }))}
      placeholder="Private email"
      className="luxury-input"
      required
      data-testid="checkout-donor-email-input"
    />
    <div className="sm:col-span-2 flex flex-col gap-3 rounded-[28px] border border-white/10 bg-black/30 p-4 text-sm text-zinc-300">
      <div className="flex items-center justify-between gap-4">
        <span data-testid="checkout-selected-tier-name">{activeTier?.name}</span>
        <span className="font-display text-xl text-white" data-testid="checkout-selected-tier-amount">
          {formatCurrency(activeTier?.amount || 0)}
        </span>
      </div>
      <p data-testid="checkout-selected-tier-impact">{activeTier?.impact}</p>
    </div>
    <Button
      type="submit"
      className="luxury-button sm:col-span-2"
      disabled={isLoading}
      data-testid="secure-checkout-submit-button"
    >
      {isLoading ? "Preparing secure checkout\u2026" : "Continue to Stripe"}
    </Button>
  </motion.form>
);

const PledgeForm = ({ activeTier, form, setForm, isLoading, onSubmit }) => (
  <motion.form
    layout
    onSubmit={onSubmit}
    className="grid gap-4 sm:grid-cols-2"
    data-testid="pledge-form"
  >
    <Input
      value={form.donor_name}
      onChange={(e) => setForm((c) => ({ ...c, donor_name: e.target.value }))}
      placeholder="Donor name"
      className="luxury-input"
      required
      data-testid="pledge-donor-name-input"
    />
    <Input
      type="email"
      value={form.donor_email}
      onChange={(e) => setForm((c) => ({ ...c, donor_email: e.target.value }))}
      placeholder="Private email"
      className="luxury-input"
      required
      data-testid="pledge-donor-email-input"
    />
    <Input
      value={form.organization}
      onChange={(e) => setForm((c) => ({ ...c, organization: e.target.value }))}
      placeholder="Family office / foundation"
      className="luxury-input"
      data-testid="pledge-organization-input"
    />
    <div className="rounded-[28px] border border-white/10 bg-black/30 px-4 py-3 text-sm text-zinc-300" data-testid="pledge-selected-tier-summary">
      {activeTier?.name} · {formatCurrency(activeTier?.amount || 0)}
    </div>
    <Textarea
      value={form.message}
      onChange={(e) => setForm((c) => ({ ...c, message: e.target.value }))}
      placeholder="Share preferred timing, discretion requests, or how your office would like follow-up handled."
      className="luxury-input min-h-36 sm:col-span-2"
      required
      data-testid="pledge-message-input"
    />
    <Button
      type="submit"
      className="luxury-button sm:col-span-2"
      disabled={isLoading}
      data-testid="pledge-submit-button"
    >
      {isLoading ? "Securing concierge follow-up\u2026" : "Reserve pledge consultation"}
    </Button>
  </motion.form>
);

const DonorLedger = ({ metrics }) => (
  <div className="space-y-4">
    <div className="rounded-[32px] border border-[#C5A059]/30 bg-[#140d05] p-6 shadow-[0_0_40px_rgba(197,160,89,0.12)]">
      <p className="text-xs uppercase tracking-[0.28em] text-[#C5A059]" data-testid="donor-ledger-kicker">
        Tonight's donor ledger
      </p>
      <div className="mt-5 space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-zinc-300">Committed this edition</span>
          <span className="font-display text-3xl text-white" data-testid="donor-ledger-total-committed">
            {formatCurrency(metrics?.total_committed_amount || 0)}
          </span>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-[24px] border border-white/10 bg-black/20 p-4">
            <p className="text-xs uppercase tracking-[0.24em] text-zinc-500">Confirmed</p>
            <p className="mt-2 font-display text-2xl text-white" data-testid="donor-ledger-confirmed-paid">
              {formatCurrency(metrics?.confirmed_paid_amount || 0)}
            </p>
          </div>
          <div className="rounded-[24px] border border-white/10 bg-black/20 p-4">
            <p className="text-xs uppercase tracking-[0.24em] text-zinc-500">Concierge pledges</p>
            <p className="mt-2 font-display text-2xl text-white" data-testid="donor-ledger-pledged-amount">
              {formatCurrency(metrics?.pledged_amount || 0)}
            </p>
          </div>
        </div>
      </div>
    </div>

    <div className="glass-panel rounded-[32px] p-6">
      <p className="luxury-kicker" data-testid="donor-qualification-kicker">
        Qualification note
      </p>
      <ul className="mt-4 space-y-3 text-sm text-zinc-300">
        <li data-testid="qualification-item-minimum">• First-time donor access begins at $100,000.</li>
        <li data-testid="qualification-item-checkout">• Stripe checkout is hosted offsite for secure payment processing.</li>
        <li data-testid="qualification-item-concierge">• Concierge pledges are reviewed privately for donor follow-up.</li>
      </ul>
    </div>
  </div>
);

/* ── Main Component ── */
export default function DonationPortal({ metrics, onRefreshMetrics, tiers = [] }) {
  const [activeTab, setActiveTab] = useState("checkout");
  const [selectedTier, setSelectedTier] = useState("");
  const [checkoutForm, setCheckoutForm] = useState({ donor_name: "", donor_email: "" });
  const [pledgeForm, setPledgeForm] = useState({
    donor_name: "",
    donor_email: "",
    organization: "",
    message: "",
  });
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [isPledgeLoading, setIsPledgeLoading] = useState(false);
  const [spotlight, setSpotlight] = useState({ x: 50, y: 50 });

  useEffect(() => {
    if (tiers.length && !selectedTier) {
      setSelectedTier(tiers[0].id);
    }
  }, [selectedTier, tiers]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeTier = useMemo(
    () => tiers.find((t) => t.id === selectedTier) || tiers[0],
    [selectedTier, tiers],
  );

  const handleSpotlight = (event) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - bounds.left) / bounds.width) * 100;
    const y = ((event.clientY - bounds.top) / bounds.height) * 100;
    setSpotlight({ x, y });
  };

  const startCheckout = useCallback(async (event) => {
    event.preventDefault();
    if (!activeTier) return;
    setIsCheckoutLoading(true);

    try {
      const response = await fetch(`${API}/payments/checkout/session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...checkoutForm,
          tier_id: activeTier.id,
          origin_url: window.location.origin,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || "Unable to begin secure checkout.");
      }

      toast.success("Redirecting to secure checkout.");
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.message || "Unable to begin secure checkout.");
    } finally {
      setIsCheckoutLoading(false);
    }
  }, [activeTier, checkoutForm]);

  const submitPledge = useCallback(async (event) => {
    event.preventDefault();
    if (!activeTier) return;
    setIsPledgeLoading(true);

    try {
      const response = await fetch(`${API}/pledges`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...pledgeForm,
          tier_id: activeTier.id,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || "Unable to reserve pledge concierge follow-up.");
      }

      toast.success(`Concierge pledge reserved at ${formatCurrency(data.pledge_amount)}.`);
      setPledgeForm({ donor_name: "", donor_email: "", organization: "", message: "" });
      onRefreshMetrics?.();
    } catch (err) {
      toast.error(err.message || "Unable to reserve pledge concierge follow-up.");
    } finally {
      setIsPledgeLoading(false);
    }
  }, [activeTier, pledgeForm, onRefreshMetrics]);

  return (
    <section
      id="donation-portal"
      data-testid="donation-portal-section"
      className="section-shell"
    >
      <div
        className="glass-panel relative overflow-hidden border border-white/10 p-6 sm:p-8 lg:p-10"
        onMouseMove={handleSpotlight}
        data-testid="donation-portal-panel"
        style={{
          backgroundImage: `radial-gradient(circle at ${spotlight.x}% ${spotlight.y}%, rgba(197, 160, 89, 0.2), transparent 28%)`,
        }}
      >
        <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="space-y-6">
            <div className="space-y-3">
              <p className="luxury-kicker" data-testid="donation-portal-kicker">
                Secure allocation
              </p>
              <h2 className="font-display text-4xl tracking-tight text-white sm:text-5xl" data-testid="donation-portal-heading">
                Reserved giving begins at {formatCurrency(metrics?.minimum_first_time_donation || MINIMUM_DONATION)}.
              </h2>
              <p className="max-w-2xl text-sm text-zinc-300 sm:text-base" data-testid="donation-portal-description">
                Choose a verified giving level below. For donors preferring a more discreet route, the concierge pledge desk can confirm participation privately.
              </p>
            </div>

            <TierSelector tiers={tiers} activeTier={activeTier} onSelect={setSelectedTier} />

            <div className="flex flex-wrap gap-3">
              <Button
                type="button"
                data-testid="checkout-tab-button"
                onClick={() => setActiveTab("checkout")}
                className={activeTab === "checkout" ? "luxury-button" : "luxury-button ghost-luxury"}
              >
                Secure checkout
              </Button>
              <Button
                type="button"
                data-testid="pledge-tab-button"
                onClick={() => setActiveTab("pledge")}
                className={activeTab === "pledge" ? "luxury-button" : "luxury-button ghost-luxury"}
              >
                Concierge pledge
              </Button>
            </div>

            {activeTab === "checkout" ? (
              <CheckoutForm
                activeTier={activeTier}
                form={checkoutForm}
                setForm={setCheckoutForm}
                isLoading={isCheckoutLoading}
                onSubmit={startCheckout}
              />
            ) : (
              <PledgeForm
                activeTier={activeTier}
                form={pledgeForm}
                setForm={setPledgeForm}
                isLoading={isPledgeLoading}
                onSubmit={submitPledge}
              />
            )}
          </div>

          <DonorLedger metrics={metrics} />
        </div>
      </div>
    </section>
  );
}
